import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import dayjs from 'dayjs';

// Components
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import AdvancedTable from '../../components/AdvancedTable';
import AssignDoctorModal from '../../components/Modal/AssignDoctorModal';
import { plusicon, refreshicon } from '../../components/imagepath';

// Actions
import {
  GetAllServiceSlot,
  ActionUpdateServiceSlot,
} from '../../store/Actions/ServiceSlotAction';

// Utils
import { matchPermissions } from '../../utils/Permission';
import { PERMISSIONS } from '../../constants';

// Custom Hooks
import { useServiceSlotColumns } from './hooks/useServiceSlotColumns';
import { useServiceSlotFilters } from './hooks/useServiceSlotFilters';

const ServiceSlot = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector(state => state?.auth);
  const { loader } = useSelector(state => state?.ServiceSlotData);
  const matchPermissionsForUser = matchPermissions(userDetails);

  // Modal state
  const [modal, setModal] = useState({ is: false, id: '' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Permissions
  const canView = matchPermissionsForUser(PERMISSIONS.SERVICES.SLOTS);
  const canCreate = matchPermissionsForUser(PERMISSIONS.SERVICES.CREATE);
  const canUpdate = matchPermissionsForUser(PERMISSIONS.SERVICES.UPDATE);
  const canExport = matchPermissionsForUser(PERMISSIONS.SERVICES.EXPORT);

  // Status update handler
  const handleStatusUpdate = useCallback(
    (recordId, newStatus) => {
      dispatch(
        ActionUpdateServiceSlot(
          {
            Query: recordId,
            obj: { status: newStatus },
          },
          () => setRefreshTrigger(prev => prev + 1)
        )
      );
    },
    [dispatch]
  );

  // Assign doctor handler
  const handleAssignDoctor = useCallback((recordId) => {
    setModal({ is: true, id: recordId });
  }, []);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setModal({ is: false, id: '' });
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Fetch data function for AdvancedTable
  const fetchData = useCallback(
    async (params) => {
      const {
        page = 1,
        perPage = 10,
        sortField,
        sortOrder,
        search,
        status = 'Upcoming',
        customerName,
        phoneNo,
        registrationNo,
        serviceName,
        date,
      } = params;

      // Build query string
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', perPage);

      if (status) queryParams.append('status', status);
      if (sortField) queryParams.append('sortOn', sortField);
      if (sortOrder) queryParams.append('sort', sortOrder);
      if (search) queryParams.append('search', search);
      if (customerName) queryParams.append('customerName', customerName);
      if (phoneNo) queryParams.append('phoneNo', phoneNo);
      if (registrationNo) queryParams.append('registrationNo', registrationNo);
      if (serviceName) queryParams.append('serviceName', serviceName);

      // Handle date filter - convert to DD-MM-YYYY format
      if (date) {
        queryParams.append('date', dayjs(date).format('DD-MM-YYYY'));
      }

      const queryString = `?${queryParams.toString()}`;

      // Return a Promise that resolves when the API call completes
      return new Promise((resolve) => {
        dispatch(
          GetAllServiceSlot({
            Query: queryString,
            callBack: (responseData) => {
              // responseData is the actual API response data
              if (responseData) {
                resolve({
                  data: responseData?.docs || [],
                  total: responseData?.totalDocs || 0,
                });
              } else {
                // Handle error case
                resolve({
                  data: [],
                  total: 0,
                });
              }
            },
          })
        );
      });
    },
    [dispatch, refreshTrigger]
  );

  // Get columns configuration
  const columns = useServiceSlotColumns({
    canUpdate,
    onStatusUpdate: handleStatusUpdate,
    onAssignDoctor: handleAssignDoctor,
  });

  // Get filter configuration
  const filterConfig = useServiceSlotFilters();

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/service-slot?page=1">Medi-Daycare Rituals Booking</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">
                    Medi-Daycare Rituals Booking List
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Advanced Table */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  {/* Page Header with Add Button */}
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center justify-content-between">
                      <div className="col-12 col-md-8">
                        <div className="doctor-table-blk">
                          <h3>Medi-Daycare Rituals Booking List</h3>
                          <div className="doctor-search-blk">
                            <div className="add-group">
                              {canCreate && (
                                <Link
                                  to="/service-slot/add-service-slot"
                                  className="btn btn-primary add-pluss ms-2"
                                >
                                  <img src={plusicon} alt="Add" />
                                </Link>
                              )}

                              <Link
                                to="/service-slot?page=1"
                                className="btn btn-primary doctor-refresh ms-2"
                              >
                                <img src={refreshicon} alt="#" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {canView ? (
                    <AdvancedTable
                      resource="service-slot"
                      columns={columns}
                      fetchData={fetchData}
                      loading={loader}
                      enableSearch={false}
                      enableFilters={true}
                      enableRefresh={true}
                      enableExport={canExport}
                      enableColumnManager={true}
                      enableHorizontalScrollFeature={true}
                      enableToolBar={true}
                      filterConfig={filterConfig}
                      defaultPageSize={10}
                      defaultFilters={{ status: 'Upcoming' }}
                      exportFileName="service-bookings"
                      persistState={true}
                      tableId="service-bookings-v2"
                      stickyHeader={true}
                      striped={true}
                      highlightOnHover={true}
                      density="comfortable"
                      key={`service-table-${refreshTrigger}`}
                    />
                  ) : (
                    <div className="permission-denied-message">
                      You do not have permission to view medi-daycare rituals bookings.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assign Doctor Modal */}
      <AssignDoctorModal
        IsOpen={modal}
        slotType="service"
        initialValues={{
          doctorId: '',
          doctorName: '',
        }}
        Isclose={handleCloseModal}
      />
    </>
  );
};

export default ServiceSlot;
